import Janus from './lib/janus.es.js';
import { createLogPrefix, processReInvite, safeJsonParse, stopAllTracks } from './lib/client-utils.js';

const template = document.createElement('template');
template.innerHTML = `
<div id="managed_audio_container"></div>
`;

/**
 * Custom web component that encapsulates a Svelte Element.
 */
export class EipJanusClient extends HTMLElement {
	constructor() {
		super();
		// Default values
		this.servers = [];
		this.iceServers = null;
		this.janus = null;
		this.helpersCount = 0;
		this.helpers = {};
		this.managedAudioMode = false;

		this.defaultCall = {
			sipHandle: null,
			registered: false,
			registering: false,
			calling: false,
			ringing: false,
			accepted: false,
			held: false,
			localTracks: {},
			localVideos: 0,
			remoteTracks: {},
			remoteVideos: 0
		}

		this.sipUserName = null;
		this.sipPassword = null;
		this.sipRegistrar = null;
		this.sipDisplayName = null;

		// Default opaque id.
		this.opaqueId = "eip-janus-" + Janus.randomString(12);
		this.masterId = null;

		// Attach a shadowdom.  May not be needed, but doesnt hurt..
		const shadowRoot = this.attachShadow({ mode: 'open' });
		shadowRoot.appendChild(template.content.cloneNode(true));

		this._loadPropsFromAttributes();
	}

	/**
	 * Get the managed audio container.
	 * @return {Element}
	 */
	get managedAudioContainer() {
		return this.shadowRoot.querySelector('#managed_audio_container');
	}

	/**
	 * Attributes that should be observed for changes.
	 * @return {string[]}
	 */
	static get observedAttributes() {
		return ['servers', 'ice-servers', 'opaque-id', 'sip-user-name', 'sip-password', 'sip-registrar', 'managed-audio-mode'];
	}

	/**
	 * Update properties when the attributes change.
	 * @param name The name of the attribute.
	 * @param oldValue The old value.
	 * @param newValue The new value.
	 */
	attributeChangedCallback(name, oldValue, newValue) {
		//console.log(`The property ${name} has changed.`);
		switch (name) {
			case 'servers':
				this.servers = safeJsonParse(newValue, []);
				break;
			case 'ice-servers':
				this.iceServers = safeJsonParse(newValue, null);
				break;
			case 'opaque-id':
				this.opaqueId = newValue;
				break;
			case 'sip-user-name':
				this.sipUserName = newValue;
				break;
			case 'sip-password':
				this.sipPassword = newValue;
				break;
			case 'sip-registrar':
				this.sipRegistrar = newValue;
				break;
			case 'managed-audio-mode':
				// boolean attribute set when any non empty value is present
				this.managedAudioMode = newValue !== null && newValue !== undefined;
				break;
		}
	}

	/**
	 * Get access to the janus lib.
	 * @return {(function(*): ({}|undefined))|*}
	 */
	static get Janus() {
		return Janus;
	}

	/**
	 * Initialize a janus instance.
	 * @param options
	 * @return {Promise<unknown>}
	 */
	async initialize(options = {}) {
		return new Promise((resolve, reject) => {
			// Make sure the browser supports WebRTC
			if (!Janus.isWebrtcSupported()) {
				const errorMessage = 'No WebRTC support... ';
				Janus.error(errorMessage);
				reject(errorMessage);
				return;
			}

			// If a custom call back is included, we want to delete from options to allow for internal behavior before custom.
			const customCallback = options.callback;
			if (customCallback) {
				delete options.callback;
			}

			const janusOptions = {
				debug: false,
				callback: () => {
					this._fireEvent('janus-ready');
					if (customCallback) customCallback();
					resolve({});
				},
				...options
			}

			Janus.init(janusOptions);
		});
	}
	createSession({ servers = null, iceServers }) {
		return new Promise((resolve, reject) => {
			if (this.janus) return resolve({ janus: this.janus });

			// Use the passed in servers or the local
			const server = servers || this.servers;
			this.janus = new Janus({
				server,
				iceServers,
				// Should the Janus API require authentication, you can specify either the API secret or user token here too
				//		token: "mytoken",
				//	or
				//		apisecret: "serversecret",
				success: () => {
					const janus = this.janus;
					resolve({ janus });
					this._fireEvent('janus-session-created', { janus });
				},
				error: (error) => {
					Janus.error(error);
					reject(error);
					this._fireEvent('janus-session-error', { error });
				},
				destroyed: () => {
					this._fireEvent('janus-session-destroyed');
				}
			});
		});
	}

	/**
	 * Attach a janus plugin directly to the interface.
	 * @param pluginConfig The plugin config.
	 */
	attachPlugin(pluginConfig) {
		this.janus.attach(pluginConfig);
	}

	/**
	 * Convenience method to attach a sip plugin.
	 */
	async attachSipPlugin() {
		const opaqueId = this.opaqueId;

		return new Promise((resolve, reject) => {
			this.janus.attach({
				plugin: "janus.plugin.sip",
				opaqueId,
				success: (pluginHandle) => {
					const sipHandle = pluginHandle;
					this.defaultCall.sipHandle = sipHandle;
					Janus.log("Plugin attached! (" + sipHandle.getPlugin() + ", id=" + sipHandle.getId() + ")");
					resolve({ sipHandle } );
					this._fireEvent( 'plugin-attached', { name: 'janus.plugin.sip', sipHandle })
				},
				error: (error) => {
					Janus.error("-- Error attaching plugin...", error);
					reject(error);
					this._fireEvent('plugin-error', { name: 'janus.plugin.sip', error })
				},
				consentDialog: (on) => {
					Janus.debug("Consent dialog should be " + (on ? "on" : "off") + " now");
					if (on) {
						this._fireEvent('show-consent-dialog');
					}
					else {
						this._fireEvent('hide-consent-dialog');
					}
				},
				iceState: (state) => {
					Janus.log("ICE state changed to " + state);
					this._fireEvent('plugin-ice-state', { state });
				},
				mediaState: (medium, on, mid) => {
					Janus.log("Janus " + (on ? "started" : "stopped") + " receiving our " + medium + " (mid=" + mid + ")");
					this._fireEvent('plugin-media-state', { medium, on, mid });
				},
				webrtcState: (on) => {
					Janus.log("Janus says our WebRTC PeerConnection is " + (on ? "up" : "down") + " now");
					this._fireEvent('plugin-webtrc-state', { on });
				},
				slowLink: (uplink, lost, mid) => {
					Janus.warn("Janus reports problems " + (uplink ? "sending" : "receiving") +
						" packets on mid " + mid + " (" + lost + " lost packets)");
					this._fireEvent('plugin-slow-link', { uplink, lost, mid });
				},
				onmessage: (msg, jsep) => {
					this._onMessage({ msg, jsep });
				},
				onlocaltrack: (track, on) => {
					this._handleOnLocalTrack({ track, on });
				},
				onremotetrack: (track, mid, on) => {
					this._handleOnRemoteTrack({ track, mid, on });
				},
				oncleanup: () => {
					Janus.log("::: Got a cleanup notification :::");
					this._cleanupCallHandle(this.defaultCall);
				}
			});
		});
	}

	async attachHelperSipPlugin({ helperId, helperCreated, sipUserName }) {
		const opaqueId = this.opaqueId;
		const master_id = this.masterId;
		const username = sipUserName || this.sipUserName;
		const logPrefix = createLogPrefix({ helperId });

		return new Promise((resolve, reject) => {
			this.janus.attach({
				plugin: "janus.plugin.sip",
				opaqueId,
				success: (pluginHandle) => {
					const helper = this.helpers[helperId];
					helper.sipHandle = pluginHandle;
					helper.registering = true;
					Janus.log(`${logPrefix} Plugin attached! (${pluginHandle.getPlugin()}, id=${pluginHandle.getId()})`);
					helper.sipHandle.send({
						message: {
							request: "register",
							type: "helper",
							username,	// We use the same username as the master session
							master_id	// Then we add the ID of the master session, nothing else
						}
					});
					resolve({ helperId, helper, sipUserName });
				},
				error: (error) => {
					Janus.error(`${logPrefix} -- Error attaching plugin...`, error);
					reject(error);
					this.removeSipHelper({ helperId });
				},
				consentDialog: (on) => {
					Janus.debug(`${logPrefix} Consent dialog should be ${(on ? "on" : "off")} now.`);
					if (on) {
						this._fireEvent('show-consent-dialog', { helperId });
					}
					else {
						this._fireEvent('hide-consent-dialog', { helperId });
					}
				},
				iceState: (state) => {
					Janus.log(`${logPrefix} ICE state changed to ${state}.`);
					this._fireEvent('plugin-ice-state', { state, helperId });
				},
				mediaState: (medium, on, mid) => {
					Janus.log(`${logPrefix} Janus ${(on ? "started" : "stopped")} receiving our ${medium} (mid=${mid})`);
					this._fireEvent('plugin-media-state', { medium, on, mid, helperId });
				},
				webrtcState: (on) => {
					Janus.log(`${logPrefix} Janus says our WebRTC PeerConnection is ${(on ? "up" : "down")} now.`);
					this._fireEvent('plugin-webtrc-state', { on, helperId });
				},
				slowLink: (uplink, lost, mid) => {
					Janus.warn(`${logPrefix} Janus reports problems ${(uplink ? "sending" : "receiving")} packets on mid ${mid} (${lost} lost packets)`);
					this._fireEvent('plugin-slow-link', { uplink, lost, mid, helperId });
				},
				onmessage: (msg, jsep) => {
					this._onMessage({ msg, jsep, helperId, helperCreated });
				},
				onlocaltrack: (track, on) => {
					this._handleOnLocalTrack({ track, on, helperId });
				},
				onremotetrack: (track, mid, on) => {
					this._handleOnRemoteTrack({ helperId, track, mid, on });
				},
				oncleanup: () => {
					Janus.log(`${logPrefix} ::: Got a cleanup notification :::`);
					const helper = this.helpers[helperId];
					this._cleanupCallHandle(helper);
				}
			});
		});
	}

	/**
	 * Add a sip helper.
	 * @param helperCreated Optional callback to be triggered when a helper is created.
	 * @return {Promise<{helperId: number}|{helper: *, helperId: number}>}
	 */
	async addSipHelper( { helperCreated }) {
		// The following code is only needed if you're interested in supporting multiple
		// calls at the same time. As explained in the Janus documentation, each Janus
		// handle can only do one PeerConnection at a time, which means you normally
		// cannot do multiple calls. If that's something you need (e.g., because you
		// need to do a SIP transfer, or want to be in two calls), then the SIP plugin
		// provides the so-called "helpers": basically additional handles attached to
		// the SIP plugin, and associated to your SIP identity. They can be used to
		// originate and receive calls exactly as the main handle: notice that incoming
		// calls will be rejected with a "486 Busy" if you're in a call already and there
		// are no available "helpers", which means you should add one in advance for that.
		// In this demo, creating a "helper" adds a new row for calls that looks and
		// works exactly as the default one: you can add more than one "helper", and
		// obviously the more you have, the more concurrent calls you can have.
		// Call back that can be used to perform an access when the helper is added.
		helperCreated = (typeof helperCreated == "function") ? helperCreated : Janus.noop;
		this.helpersCount++;
		const helperId = this.helpersCount;
		this.helpers[helperId] = {
			id: helperId,
			localTracks: {},
			localVideos: 0,
			remoteTracks: {},
			remoteVideos: 0,
			registering: false,
			registered: false,
			ringing: false,
			calling: false
		};

		// Attach to SIP plugin, but only register as a helper for the master session
		try {
			await this.attachHelperSipPlugin({ helperId, helperCreated });
			const helpers = this.helpers;
			const helper = helpers[helperId];
			return { helperId, helper, helpers };
		}
		catch (e) {
			return { helperId: -1 };
		}
	}

	/**
	 * Remove a sip helper.
	 * @param helperId The helper id.
	 */
	async removeSipHelper({ helperId }) {
		return new Promise((resolve, reject) => {
			const helpers = this.helpers;
			const helper = helpers[helperId];
			if (!helper) {
				return resolve({ helperId, helpers });
			}

			if (helper.sipHandle) {
				// Detach from the helper's Janus handle
				helper.sipHandle.detach();
			}
			delete helpers[helperId];
			resolve({ helperId, helpers });
			this._fireEvent('helper-removed', { helperId, helpers });
		});
	}

	/**
	 * Perform a call.
	 * @param handle The sip handle.
	 * @param uri The uri.
	 * @param doVideo The flag to request video.
	 * @param referId The optional refer id.
	 * @param autoAcceptInvites The auto accept invites flag.
	 * @return {Promise<unknown>}
	 */
	async performCall({ handle, uri, doVideo, referId, autoAcceptInvites = false }) {
		return new Promise((resolve, reject) => {

			// We want bidirectional audio for sure, and maybe video
			handle.doAudio = true;
			handle.doVideo = doVideo;

			let tracks = [{ type: 'audio', capture: true, recv: true }];
			if (doVideo)
				tracks.push({ type: 'video', capture: true, recv: true });

			handle.createOffer({
				tracks,
				success(jsep) {
					Janus.debug("Got SDP!", jsep);
					// By default, you only pass the SIP URI to call as an
					// argument to a "call" request. Should you want the
					// SIP stack to add some custom headers to the INVITE,
					// you can do so by adding an additional "headers" object,
					// containing each of the headers as key-value, e.g.:
					//		var body = { request: "call", uri: jq('#peer').val(),
					//			headers: {
					//				"My-Header": "value",
					//				"AnotherHeader": "another string"
					//			}
					//		};
					let body = { request: "call", uri: uri };
					// Note: you can also ask the plugin to negotiate SDES-SRTP, instead of the
					// default plain RTP, by adding a "srtp" attribute to the request. Valid
					// values are "sdes_optional" and "sdes_mandatory", e.g.:
					//		var body = { request: "call", uri: jq('#peer').val(), srtp: "sdes_optional" };
					// "sdes_optional" will negotiate RTP/AVP and add a crypto line,
					// "sdes_mandatory" will set the protocol to RTP/SAVP instead.
					// Just beware that some endpoints will NOT accept an INVITE
					// with a crypto line in it if the protocol is not RTP/SAVP,
					// so if you want SDES use "sdes_optional" with care.
					// Note 2: by default, the SIP plugin auto-answers incoming
					// re-INVITEs, without involving the browser/client: this is
					// for backwards compatibility with older Janus clients that
					// may not be able to handle them. If you want to receive
					// re-INVITES to handle them yourself, specify it here, e.g.:

					// NOTE: This allows call recording to properly work
					// TODO: Still need investigate why this behaves inconsistent every once in awhile.
					// This also breaks calls from lasting longer than 1 minute!
					body["autoaccept_reinvites"] = autoAcceptInvites;
					if (referId) {
						// In case we're originating this call because of a call
						// transfer, we need to provide the internal reference ID
						body["refer_id"] = referId;
					}
					handle.send({ message: body, jsep });
					resolve({ jsep });
				},
				error(error) {
					reject(error)
				}
			});
		});
	}

	/**
	 * Perform an answer or offer depending on the request.
	 * @param handle The sip handle.
	 * @param jsep The jsep to process.
	 * @param autoAcceptInvites The auto accept invites flag.
	 * @return {Promise<unknown>}
	 */
	async performAnswerOrOffer({ handle, jsep, autoAcceptInvites = false }) {
		return new Promise((resolve, reject) => {
				const { doAudio, doVideo, offerlessInvite } = processReInvite({ jsep });
				// Notice that we can only answer if we got an offer: if this was
				// an offerless call, we'll need to create an offer ourselves
				const sipAction = (offerlessInvite ? handle.createOffer : handle.createAnswer);
				// We want bidirectional audio and/or video
				let tracks = [];
				if (doAudio) {
					tracks.push({ type: 'audio', capture: true, recv: true });
				}

				if (doVideo) {
					tracks.push({ type: 'video', capture: true, recv: true });
				}

				sipAction({
					jsep,
					tracks,
					success(jsep) {
						Janus.debug("Got SDP " + jsep.type + "! audio=" + doAudio + ", video=" + doVideo + ":", jsep);
						handle.doAudio = doAudio;
						handle.doVideo = doVideo;
						const body = { request: "accept" };
						// Note: as with "call", you can add a "srtp" attribute to
						// negotiate/mandate SDES support for this incoming call.
						// The default behaviour is to automatically use it if
						// the caller negotiated it, but you may choose to require
						// SDES support by setting "srtp" to "sdes_mandatory", e.g.:
						//		var body = { request: "accept", srtp: "sdes_mandatory" };
						// This way you'll tell the plugin to accept the call, but ONLY
						// if SDES is available, and you don't want plain RTP. If it
						// is not available, you'll get an error (452) back. You can
						// also specify the SRTP profile to negotiate by setting the
						// "srtp_profile" property accordingly (the default if not
						// set in the request is "AES_CM_128_HMAC_SHA1_80")
						// Note 2: by default, the SIP plugin auto-answers incoming
						// re-INVITEs, without involving the browser/client: this is
						// for backwards compatibility with older Janus clients that
						// may not be able to handle them. If you want to receive
						// re-INVITES to handle them yourself, specify it here, e.g.:
						body["autoaccept_reinvites"] = autoAcceptInvites;
						handle.send({ message: body, jsep });
						resolve({ jsep });
					},
					error(error) {
						Janus.error("WebRTC error:", error);
						// Don't keep the caller waiting any longer, but use a 480 instead of the default 486 to clarify the cause
						const message = { request: "decline", code: 480 };
						handle.send({ message });
						reject(error);
					}
				});
		});
	}

	/**
	 * Send a message to the supplied handle or default.
	 * @param handle Optional handle.  Will default to use internal handle if not supplied.
	 * @param message The message to send.
	 * @param params The optional params to add in.
	 * @return {Promise<unknown>}
	 */
	async performSend({ handle = null, message = null, params = {} }) {
		const handleToUse = handle ? handle : this.defaultCall.sipHandle;
		return new Promise((resolve, reject) => {
			if (!handleToUse || !message) {
				reject('Invalid handle or message.');
				return;
			}
			handleToUse.send({ message, ...params });
			resolve({ handle, message });
		});
	}

	/**
	 * Perform a hangup.
	 * @param handle The handle to operate on.
	 * @param referId The optional referId.
	 * @return {Promise<unknown>}
	 */
	async performHangup({ handle }) {
		return new Promise((resolve, reject) => {
			if (!handle) {
				reject();
				return;
			}

			const message = { request: "hangup" };
			handle.send({ message });
			handle.hangup();
			resolve({ handle });
		});
	}

	/**
	 * Perform a decline.
	 * @param handle The handle to operate on.
	 * @param referId The optional referId.
	 * @return {Promise<unknown>}
	 */
	async performDecline({ handle, referId = null }) {
		return new Promise((resolve, reject) => {
			if (!handle) {
				reject();
				return;
			}

			const message = { request: "decline" };

			// Optional refer id.
			if (referId) {
				message.refer_id = referId;
			}
			handle.send({ message });
			resolve({ handle });
		});
	}

	/**
	 * Perform a hold.
	 * @param handle The sip handle to operate on.
	 * @param direction The direction. sendonly, recvonly, inactive
	 * @return {Promise<unknown>}
	 */
	async performHold({ handle, direction = "sendonly" }) {
		return new Promise((resolve, reject) => {
			if (!handle) {
				reject();
				return;
			}

			const message = { request: "hold" };
			handle.send({ message, direction });
			resolve({ handle });
		});
	}

	/**
	 * Perform an unhold.
	 * @param handle The sip handle to operate on.
	 * @return {Promise<unknown>}
	 */
	async performUnHold({ handle }) {
		return new Promise((resolve, reject) => {
			if (!handle) {
				reject();
				return;
			}

			const message = { request: "unhold" };
			handle.send({ message });
			resolve({ handle });
		});
	}

	/**
	 * Send DTMF tones (Dual-Tone Multi-Frequency signaling) or SIP INFO messages.
	 *
	 * Key Differences
	 * Feature              | DTMF Signaling Tones (Inband)          | dtmf_info Request (Out-of-Band)
	 * ------------------------------------------------------------------------------------------------------
	 * Transmission Medium  | Audio stream                           | Signaling/control channel
	 * Encoding             | Audio frequencies (tones)              | Data/message format (e.g., SIP INFO)
	 * Reliability          | Affected by codec and audio quality    | Unaffected by audio; more reliable
	 * Compatibility        | Legacy telephony systems               | Modern VoIP/WebRTC systems
	 * Complexity           | Simple                                 | Requires signaling protocol support
	 *
	 * @param handle {object} The sip handle to operate on.
	 * @param digit {string} The digit or digits to send. E.g. 0-9, *, #
	 * @param inband {boolean} The flag to send as inband. False for outband.
	 * @return {Promise<unknown>}
	 */
	async performSendDtmf({ handle, digit, inband = true }) {
		return new Promise((resolve, reject) => {
			if (!handle) {
				reject();
				return;
			}

			if (inband) {
				// Dual-Tone Multi-Frequency signaling (DTMF) tones.
				handle.dtmf({
					dtmf: {
						tones: digit,
						duration: 100,      // Duration of the DTMF tone in milliseconds (optional, default is 70ms)
						gap: 50             // Gap between DTMF tones if multiple tones are specified (optional, default is 50ms)
					},
					success: () => resolve({ handle, digit, message: 'DTMF request sent successfully' }),
					error: error =>  reject({ handle, digit, error })
				});
			}
			else {
				// SIP INFO messages.
				handle.send({
					message: {
						request: "dtmf_info",
						digit,                  // The DTMF digit to send
						duration: 100,          // Optional: Duration of the DTMF tone in milliseconds
					},
					success: resolve({ handle, digit, message: 'dtmf_info request sent successfully' }),
					error: error =>  reject({ handle, digit, error })
				});
			}
		});
	}

	/**
	 * Destroy the internal janus instance.
	 */
	destroy() {
		if (this.janus) {
			this.janus.destroy();
		}
	}

	/**
	 * Handle a message.
	 * @param msg The msg.
	 * @param jsep The jsep.
	 * @param helperId The optional helperId
	 * @param helperCreated Optional call back triggered after a helper is created/registered.
	 * @private
	 */
	_onMessage({ msg, jsep, helperId, helperCreated }) {
		const logPrefix = createLogPrefix({ helperId });
		Janus.debug(`${logPrefix} ::: Got a message :::`, msg);
		const helpers = this.helpers;
		const helper = helpers[helperId];
		const defaultCall = this.defaultCall;
		// The active sip call will either be a helper or the default call.
		const activeCall = helper ? helper : defaultCall;
		const error = msg.error;

		// Any message that contains an error will send an error event and omit a regular message.
		if (error) {
			this._fireEvent('plugin-error-message', { msg, jsep, error, helperId, helpers, activeCall, defaultCall });
			return;
		}

		// Process the event and set any internal state data
		// This is primarily used to make it more convenient for client implementations.
		// This is also being used to handle any sip interactions that are assumed so we don't need to worry about it in the different implementations.
		this._processEvent({ msg, activeCall, jsep, helperId, helperCreated });

		// This effectively provides the current state information to the clients.
		this._fireEvent('plugin-message', { msg, jsep, helperId, helpers, activeCall, defaultCall });
	}

	/**
	 * Process the event and perform any logic that we want to occur before we hande of to client.
	 * @param msg The message to process.
	 * @param activeCall The active sip call. Helper or default.
	 * @param jsep The jsep.
	 * @param helperId The optional helper id depending on the context.
	 * @param helperCreated The optional call back called when a helper is created.
	 * @private
	 */
	_processEvent({ msg, activeCall, jsep, helperId, helperCreated }) {
		const result = msg["result"];
		const callId = msg["call_id"];
		const logPrefix = createLogPrefix({ helperId });
		if (result && result["event"]) {
			const event = result["event"];
			if (event === 'registration_failed') {
				Janus.warn("Registration failed: " + result["code"] + " " + result["reason"]);
				activeCall.registered = false;
				activeCall.registering = false;
				return;
			}
			if (event === 'registered') {
				Janus.log(`${logPrefix} Successfully registered as ${result["username"]}!`);
				activeCall.registered = true;
				activeCall.registering = false;
				// Used for created helper sessions
				if (result["master_id"]){
					this.masterId = result["master_id"];
				}
				// Optional callback function called when the helper is created
				if (helperCreated) helperCreated(helperId);
			}
			else if (event === 'calling') {
				activeCall.calling = true;
			}
			else if (event === 'incomingcall') {
				Janus.log(`${logPrefix} Incoming call from ${result["username"]}!`);
				activeCall.sipHandle.callId = callId;
				activeCall.ringing = true;
			}
			else if (event === 'accepting') {
				Janus.log(`${logPrefix} ${result["username"]} accepted the call!", jsep`);
				// Response to an offerless INVITE, let's wait for an 'accepted'
			}
			else if (event === 'progress') {
				Janus.log(`${logPrefix} There's early media from ${result["username"]}, waiting for the call!`, jsep);
				// Call can start, now: handle the remote answer
				this._handleRemoteJsep({ activeCall, jsep });
			}
			else if (event === 'accepted') {
				activeCall.ringing = false;
				activeCall.calling = false;
				activeCall.accepted = true;
				activeCall.sipHandle.callId = callId;
				// Call can start, now: handle the remote answer
				this._handleRemoteJsep({ activeCall, jsep });
			}
			else if (event === 'updatingcall') {
				this._handleUpdatingCallEvent({ msg, activeCall, jsep, helperId })
			}
			else if (event === 'message') {
			}
			else if (event === 'info') {
			}
			else if (event === 'notify') {
			}
			else if (event === 'transfer') {
			}
			else if (event === 'hangup') {
				activeCall.ringing = false;
				activeCall.calling = false;
			}
			else if (event === 'holding') {
				activeCall.held = true;
			}
			else if (event === 'resuming') {
				activeCall.held = false;
			}
			else if (event === 'messagedelivery') {
			}
		}
	}

	/**
	 * Handle a local track change for a helper or default call.
	 * @param helperId The optional helper id.
	 * @param track The remote track.
	 * @param on The on.
	 * @private
	 */
	_handleOnLocalTrack({ track, on, helperId }) {
		const logPrefix = createLogPrefix({ helperId });
		Janus.debug(`${logPrefix} Local track ${(on ? "added" : "removed")}:`, track);
		const helpers = this.helpers;
		const helper = helpers[helperId];
		const defaultCall = this.defaultCall;
		const activeCall = helper ? helper : defaultCall;

		// We use the track ID as name of the element, but it may contain invalid characters
		const trackId = track.id.replace(/[{}]/g, "");
		const stream = activeCall.localTracks[trackId];
		if (!on) {
			// Track removed, get rid of the stream and the rendering
			stopAllTracks(stream);
			if (track.kind === "video") {
				activeCall.localVideos--;
			}
			delete activeCall.localTracks[trackId];
			this._fireEvent('plugin-local-track-removed', { helperId, helpers, defaultCall, activeCall, track, trackId });
			return;
		}

		if (stream) {
			// We've been here already
			return;
		}

		const iceConnectionState = activeCall.sipHandle.webrtcStuff.pc.iceConnectionState;

		// If we're here, a new track was added
		// Audio ignored, it would create echo...
		if (track.kind === "video") {
			// New video track: create a stream out of it
			activeCall.localVideos++;
			const stream = new MediaStream([track]);
			activeCall.localTracks[trackId] = stream;
			Janus.log(`${logPrefix} Created local stream:`, stream);
			// Expected for end users to attach the media to some element...
			this._fireEvent('plugin-local-track-added', {
				helperId, helpers, track, trackId, stream,
				iceConnectionState
			});
		}

		if (iceConnectionState !== "completed" && iceConnectionState !== "connected") {
		}
	}

	/**
	 * Handle a remote track change for a helper or default call.
	 * @param helperId The optional helper id.
	 * @param track The remote track.
	 * @param mid The mid.
	 * @param on The on.
	 * @private
	 */
	_handleOnRemoteTrack({ track, mid, on, helperId }) {
		const logPrefix = createLogPrefix({ helperId });
		Janus.debug(`${logPrefix} Remote track (mid=" + mid + ") " + (on ? "added" : "removed"):`, track);
		const helpers = this.helpers;
		const helper = helpers[helperId];
		const defaultCall = this.defaultCall;
		const activeCall = helper ? helper : defaultCall;
		const managedAudioMode = this.managedAudioMode;

		if (!on) {
			// Track removed, get rid of the stream and the rendering
			const stream = activeCall.remoteTracks[mid];
			stopAllTracks(stream);
			if (track.kind === "audio") {
				if (managedAudioMode) {
					const audioElementId = helperId ? `remote_audio_${helperId}_m${mid}` : `remote_audio_m${mid}`;
					const audioElement = this.shadowRoot.querySelector(`#${audioElementId}`);
					if (audioElement) {
						audioElement.remove();
					}
				}
			}
			else if (track.kind === "video") {
				activeCall.remoteVideos--;
			}
			delete activeCall.remoteTracks[mid];

			this._fireEvent('plugin-remote-track-removed', { helperId, helpers, defaultCall, activeCall, track, mid });
			return;
		}

		// If we're here, a new track was added
		const remoteTrackArray = Object.keys(activeCall.remoteTracks);
		if (remoteTrackArray.length === 0) {
			// Maybe perform some logic here??
		}

		const stream = new MediaStream([track]);
		activeCall.remoteTracks[mid] = stream;
		if (track.kind === "audio") {
			Janus.log(`${logPrefix} Created remote audio stream:`, stream);
			// When managed audio mode is enabled, audio elements will be internally created and attached to.
			if (managedAudioMode) {
				const audioElementId = helperId ? `remote_audio_${helperId}_m${mid}` : `remote_audio_m${mid}`;
				let audioElement = this.managedAudioContainer.querySelector(`#${audioElementId}`);
				if (!audioElement) {
					this.managedAudioContainer
						.insertAdjacentHTML('beforeend', `<audio style="display: none;" id="${audioElementId}" autoplay playsinline/>`);
					audioElement = this.managedAudioContainer.querySelector(`#${audioElementId}`);
				}

				Janus.attachMediaStream(audioElement, stream);
			}
		}
		else {
			// New video track: create a stream out of it
			activeCall.remoteVideos++;
			Janus.log(`${logPrefix} Created remote video stream:`, stream);
		}

		this._fireEvent('plugin-remote-track-added', {
			helperId, helpers, defaultCall, activeCall, track, mid, stream,
			managedAudioMode
		});
	}

	/**
	 * Handle the remote jsep.
	 * @param activeCall The active call.  Helper or defalt.
	 * @param jsep The jsep to handle.
	 * @private
	 */
	_handleRemoteJsep({ activeCall, jsep }) {
		// Call can start, now: handle the remote answer
		if (jsep) {
			const handle = activeCall.sipHandle;
			handle.handleRemoteJsep({
				jsep, error: () => {
					this.performHangup({ handle }).then(() => {
						// Successful hangup...
					});
				}
			});
		}
	}

	_handleUpdatingCallEvent({ msg, jsep, activeCall, helperId }) {
		// We got a re-INVITE: while we may prompt the user (e.g.,
		// to notify about media changes), to keep things simple
		// we just accept the update and send an answer right away
		const logPrefix = createLogPrefix({ helperId });
		Janus.log(`${logPrefix} Got re-INVITE`);
		const { doAudio, doVideo, offerlessInvite } = processReInvite({ jsep });
		const sipHandle = activeCall.sipHandle;
		// We want bidirectional audio and/or video, but only
		// populate tracks if we weren't sending something before
		let tracks = [];
		if (doAudio && !sipHandle.doAudio) {
			sipHandle.doAudio = true;
			tracks.push({ type: 'audio', capture: true, recv: true });
		}
		if (doVideo && !sipHandle.doVideo) {
			sipHandle.doVideo = true;
			tracks.push({ type: 'video', capture: true, recv: true });
		}
		const reInviteRequest = {
			jsep,
			tracks,
			success(jsep) {
				Janus.debug(`${logPrefix} Got SDP ${jsep.type}! audio=${doAudio}, video=${doVideo}:`, jsep);
				const body = { request: "update" };
				sipHandle.send({ message: body, jsep });
			},
			error(error) {
				Janus.error("WebRTC error:", error);
				//TODO: Possible event here..
			}
		};

		if (!offerlessInvite) {
			sipHandle.createAnswer(reInviteRequest);
		}
		else {
			sipHandle.createOffer(reInviteRequest);
		}
	}

	/**
	 * Helper to fire dom custom event.
	 * @param eventName The name of the event.
	 * @param detail The detail of the event.
	 * @private
	 */
	_fireEvent(eventName, detail = null) {
		this.dispatchEvent(new CustomEvent(eventName, { bubbles: true, detail }))
	}

	/**
	 * Cleanup a call handle. This may be a helper or the default call.
	 * @param callHandle The call handle.
	 * @private
	 */
	_cleanupCallHandle(callHandle) {
		if (callHandle) {
			if (callHandle.sipHandle) callHandle.sipHandle.callId = null;

			callHandle.calling = false;
			callHandle.ringing = false;
			callHandle.held = false
			callHandle.accepted = false;
			callHandle.localTracks = {};
			callHandle.localVideos = 0;
			callHandle.remoteTracks = {};
			callHandle.remoteVideos = 0;
		}

		// Send a cleanup event with the current state
		this._fireEvent('plugin-cleanup', { defaultCall: this.defaultCall, helpers: this.helpers });
	}

	/**
	 * Load properties from attributes.
	 * @private
	 */
	_loadPropsFromAttributes() {
		// Initial attributes used for configuration.
		const servers = this.getAttribute('servers');
		const iceServers = this.getAttribute('ice-servers');
		const opaqueId = this.getAttribute('opaque-id');
		const sipUserName = this.getAttribute('sip-user-name');
		const sipPassword = this.getAttribute('sip-password');
		const sipRegistrar = this.getAttribute('sip-registrar');
		const managedAudioMode = this.getAttribute('managed-audio-mode');

		if (servers) {
			this.servers = safeJsonParse(servers, []);
		}

		if (iceServers) {
			this.iceServers = safeJsonParse(iceServers);
		}

		if (opaqueId) {
			this.opaqueId = opaqueId;
		}

		if (sipUserName) {
			this.sipUserName = sipUserName;
		}

		if (sipPassword) {
			this.sipPassword = sipPassword;
		}

		if (sipRegistrar) {
			this.sipRegistrar = sipRegistrar;
		}

		if (managedAudioMode) {
			this.managedAudioMode = managedAudioMode;
		}
	}

	disconnectedCallback() {
		this.janus = null;
	}
}

customElements.define('eip-janus-client', EipJanusClient);
